if (!customElements.get('related-products-replacement')) {
  class RelatedProductsReplacement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized !== 'true') {
        this.dataset.initialized = 'true';
        this.replacementFrame = requestAnimationFrame(() => this.replaceFixedCollection());
      }

      this.startProductFill();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.replacementFrame);
      clearTimeout(this.fillTimer);
      clearTimeout(this.fallbackTimer);

      if (this.recommendationsObserver) {
        this.recommendationsObserver.disconnect();
      }
    }

    replaceFixedCollection() {
      const targetSuffix = this.dataset.targetSectionSuffix;
      if (!targetSuffix || !/^[A-Za-z0-9_-]+$/.test(targetSuffix)) return;

      const targetSection = document.querySelector(`[id^="shopify-section-"][id$="__${targetSuffix}"]`);
      const previousInstances = Array.from(
        document.querySelectorAll('related-products-replacement[data-relocated="true"]')
      ).filter((instance) => instance !== this);

      if (!targetSection) {
        const previousInstance = previousInstances[0];

        if (!previousInstance) {
          this.remove();
          return;
        }

        previousInstance.before(this);
        this.dataset.relocated = 'true';
        previousInstances.forEach((instance) => instance.remove());
        return;
      }

      if (targetSection.contains(this)) return;

      this.dataset.relocated = 'true';
      targetSection.before(this);
      previousInstances.forEach((instance) => instance.remove());

      // Keep a hidden, stable anchor for future product or variant swaps.
      targetSection.hidden = true;
      targetSection.setAttribute('aria-hidden', 'true');
      targetSection.style.display = 'none';
    }

    startProductFill() {
      this.recommendationsElement = this.querySelector('product-recommendations');
      if (!this.recommendationsElement) return;

      if (this.recommendationsObserver) {
        this.recommendationsObserver.disconnect();
      }

      this.recommendationsObserver = new MutationObserver(() => this.queueFill());
      this.recommendationsObserver.observe(this.recommendationsElement, {
        childList: true,
        subtree: true,
      });

      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = setTimeout(() => this.fillToTarget(true), 2500);
      this.queueFill();
    }

    queueFill() {
      clearTimeout(this.fillTimer);
      this.fillTimer = setTimeout(() => this.fillToTarget(false), 60);
    }

    getTargetCount() {
      const parsedCount = Number.parseInt(this.dataset.targetCount, 10);
      return Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : 16;
    }

    getGridItemCount(list) {
      return Array.from(list.children).filter((item) => item.classList.contains('grid__item')).length;
    }

    getExistingProductIds(list) {
      return new Set(
        Array.from(list.querySelectorAll('[data-product-id]'))
          .map((item) => item.dataset.productId)
          .filter(Boolean)
      );
    }

    createProductGrid() {
      const heading = document.createElement('h2');
      heading.className = 'related-products__heading inline-richtext h2';
      heading.textContent = 'You may also like';

      const list = document.createElement('ul');
      list.className = 'grid product-grid grid--4-col-desktop grid--2-col-tablet-down';
      list.setAttribute('role', 'list');

      this.recommendationsElement.append(heading, list);
      return list;
    }

    async fillToTarget(allowEmptyRecommendations) {
      const targetCount = this.getTargetCount();
      let list = this.recommendationsElement.querySelector('ul.product-grid');

      if (list && this.getGridItemCount(list) >= targetCount) return;
      if (!list && !allowEmptyRecommendations) return;

      const latestCards = await this.loadLatestCards();
      if (!latestCards.length || !this.isConnected) return;

      list = this.recommendationsElement.querySelector('ul.product-grid');
      if (!list) {
        if (!allowEmptyRecommendations) return;
        list = this.createProductGrid();
      }

      const currentProductId = this.dataset.currentProductId;
      const excludedProductIds = this.getExistingProductIds(list);

      if (currentProductId) {
        excludedProductIds.add(currentProductId);
      }

      for (const sourceCard of latestCards) {
        if (this.getGridItemCount(list) >= targetCount) break;

        const productId = sourceCard.dataset.productId;
        if (!productId || excludedProductIds.has(productId)) continue;

        list.append(sourceCard.cloneNode(true));
        excludedProductIds.add(productId);
      }
    }

    loadLatestCards() {
      if (this.latestCardsPromise) return this.latestCardsPromise;

      const sectionId = this.dataset.latestSectionId;
      if (!sectionId || !/^[A-Za-z0-9_-]+$/.test(sectionId)) {
        return Promise.resolve([]);
      }

      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}collections/all`, window.location.origin);
      url.searchParams.set('sections', sectionId);
      url.searchParams.set('sort_by', 'created-descending');

      this.latestCardsPromise = fetch(url.toString())
        .then((response) => {
          if (!response.ok) throw new Error(`Latest products request failed: ${response.status}`);
          return response.json();
        })
        .then((sections) => {
          const sectionHtml = sections[sectionId];
          if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) return [];

          const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
          return Array.from(documentFragment.querySelectorAll('[data-latest-product-card]'));
        })
        .catch((error) => {
          console.warn('Could not load the newest products for You may also like.', error);
          return [];
        });

      return this.latestCardsPromise;
    }
  }

  customElements.define('related-products-replacement', RelatedProductsReplacement);
}
